import type { NewsArticle } from './newsData';

export const newsArchive2024: NewsArticle[] = [
  {
    slug: 'essential-course-opening-guide-202403',
    date: '2024年3月8日',
    category: 'schedule',
    categoryLabel: '開校情報',
    title: '定員6名の教室で起きる、個別指導の具体例',
    metaDescription:
      '定員6名・最少催行3名の少人数制で、講師が一人ひとりの所作をどこまで見られるのか。お台場本校の実技時間の使い方、声かけのタイミング、復習の残し方まで、個別指導の現場を具体的に紹介します。',
    keywords: ['少人数制', '個別指導', '定員6名', '執事養成', '実技指導', 'お台場本校'],
    heroImage: '/images/generated/about-classroom-2026.jpg',
    heroAlt: '少人数の受講生が講師の間近で所作を確認している講義室',
    figures: [
      {
        src: '/images/generated/training-basic-2026.jpg',
        alt: '基礎の立ち方を講師が横から確認している実技の場面',
        caption: '人数が少ないからこそ、姿勢のわずかな傾きまでその場で指摘できる',
      },
    ],
    overview: {
      heading: '少人数制・個別指導の要点',
      rows: [
        { label: '定員', value: '6名（最少催行3名）' },
        { label: '1日の構成', value: '6時間（90分×4コマ）' },
        { label: '開催', value: '土日開催' },
        { label: '指導の特徴', value: '実技中の即時フィードバックと個人メモ' },
        { label: '会場', value: 'お台場本校' },
      ],
    },
    faq: [
      {
        question: '大人数の講座と何が違いますか？',
        answer:
          '実技の最中に講師が巡回し、立ち位置・視線・手元まで個別に修正できる点です。定員6名なので、同じコマ内で全員分の確認が回ります。',
      },
      {
        question: '自分の弱点だけ重点的に見てもらえますか？',
        answer:
          'はい。前回の振り返りメモを踏まえ、その日の課題を一人ずつ設定します。共通説明のあとに個人課題へ入る流れが基本です。',
      },
      {
        question: '遅れていると感じたときどうなりますか？',
        answer:
          '休憩前後の短い再演や、次コマ冒頭での再確認で追いつけるよう調整します。出席率80％以上を前提に、欠席分の補い方も個別に相談します。',
      },
    ],
    related: [
      { title: 'コース一覧', desc: '3コースの位置づけを比較', href: '/courses' },
      { title: 'カリキュラム', desc: '1日6時間の進め方', href: '/curriculum' },
      { title: '入学案内', desc: '申込条件と流れ', href: '/admission' },
      { title: '資料請求（無料）', desc: '少人数制の詳細を書面で', href: '/brochure' },
    ],
    body: `<p>「少人数」と書いてあっても、実際の教室で何が起きるのかは外から見えにくいものです。日本執事アカデミーの本科は定員6名・最少催行3名。ここでは、その人数だからこそ成立する個別指導の中身を、授業の時間配分に沿って開きます。</p>
<h2>90分コマのなかで講師が見る順番</h2>
<p>1日は6時間、90分×4コマです。冒頭15分ほどで全員に共通の型を示し、残りはペアまたは一人ずつ実演に充てます。講師は部屋を一周しながら、足の開き、肩の高さ、声の届き方を短い言葉で直します。6名までなので、同じコマ内で全員に最低一度は手が入ります。大教室では後回しになりがちな「あと2センチ内側」といった微修正が、その場で終わるのが最大の差です。</p>
<h2>個人カルテに残す観察メモ</h2>
<p>毎回の終わりに、講師は受講生ごとの観察メモを残します。前回つまずいた動作、今日安定した点、次に試す条件を三行程度で記録し、次回の冒頭で本人に共有します。教科書全15冊の該当箇所も添え、自宅での再現ポイントを明確にします。評価表のような点数化ではなく、次の動作に直結するメモであることが目的です。</p>
<h2>追いつきと先取りを同時に回す</h2>
<p>理解の速度は人によって違います。早い受講生には、同じ場面に来客の人数や時間帯の条件を足して難度を上げます。時間が必要な受講生には、動作を二つに分割し、前半だけを繰り返します。最少催行3名を下回らない範囲でクラスが組まれるため、極端に手薄になることもありません。土日開催のため平日に自主練習した内容を、次の登校日にすぐ確認できるリズムも、個別最適化を支えています。</p>
<h2>向いている人と、検討時の視点</h2>
<p>細かい指摘をその場で受けたい方、自分の癖を言語化してほしい方には相性が良い環境です。逆に、講義を聞くだけで進めたい方には密度が高く感じられるかもしれません。見学や資料で「誰が・どのタイミングで・何を直すのか」を確認したうえで検討してください。教室の空気は、人数が少ないほど正直に表れます。</p>`,
  },
  {
    slug: 'seminar-trial-consultation-202407',
    date: '2024年7月12日',
    category: 'seminar',
    categoryLabel: '説明会',
    title: '体験講座の一日：扱う内容と本科への接続点',
    metaDescription:
      '体験講座で実際に触れる単元、時間の区切り方、本科エッセンシャルとの違いを一日の流れで解説。協会認定資格や修了実技試験へどう続くのか、参加前に知っておきたい接続点を整理しました。',
    keywords: ['体験講座', '入学前体験', 'エッセンシャルコース', 'カリキュラム接続', '執事教室', '土日講座'],
    heroImage: '/images/generated/facilities-hero-2026.jpg',
    heroAlt: 'お台場本校の実習スペースで体験講座の説明を受けている様子',
    figures: [
      {
        src: '/images/generated/essential-manners-2026.jpg',
        alt: '体験講座でテーブル周りの基本動作に触れている手元',
        caption: '短い時間でも、型に触れる順序は本科と同じ骨格で組む',
      },
    ],
    overview: {
      heading: '体験講座で確認できること',
      rows: [
        { label: '主な内容', value: '立ち居振る舞い／短い接遇場面／コース説明' },
        { label: '目的', value: '本科の進め方と自分の適性を照合する' },
        { label: '会場', value: 'お台場本校' },
        { label: '接続先', value: 'エッセンシャルを起点に3コースへ' },
        { label: '申込', value: '説明会ページおよびお問い合わせ' },
      ],
    },
    faq: [
      {
        question: '体験だけで入学は決まりますか？',
        answer:
          '決まりません。体験は適性確認の場です。入学手続きは別途、入学案内の流れに沿って進めていただきます。',
      },
      {
        question: '服装や持ち物の指定はありますか？',
        answer:
          '動きやすい落ち着いた服装を推奨します。筆記用具があれば十分です。詳細は開催回ごとに事前案内します。',
      },
      {
        question: '本科の何コマ分に相当しますか？',
        answer:
          '分量としては本科1日（6時間）には達しません。ただし単元の順番と講師の直し方は、本科と同じ設計思想で体験できます。',
      },
    ],
    related: [
      { title: '説明会・セミナー', desc: '体験講座の日程と申込', href: '/seminar' },
      { title: 'コース一覧', desc: '本科3コースの違い', href: '/courses' },
      { title: 'アクセス', desc: 'お台場本校への行き方', href: '/access' },
      { title: 'お問い合わせ', desc: '回ごとの空きを確認', href: '/contact' },
    ],
    body: `<p>体験講座は「雰囲気を味わうイベント」ではなく、本科の入口を短く切り取った見本です。参加者が一日（または半日相当）で何に触れ、それがエッセンシャル／アドバンス／マスターのどこへ続くのかを、時間順に説明します。</p>
<h2>開始直後：姿勢と移動の最小セット</h2>
<p>最初に扱うのは、立ち方・お辞儀・部屋への入り方です。説明は短く、すぐ実演に移ります。ここでは上手さより、指示を動作に変換できるかを見ます。講師が使う声かけは本科と同じ語彙です。体験用にやさしく言い換えてしまうと、入学後のギャップが生まれるためです。</p>
<h2>中盤：短い接遇シーンで判断を試す</h2>
<p>来客の受付から着席案内までを、二、三分の場面に圧縮して繰り返します。正解手順を暗記させるのではなく、相手の動きに合わせて間を取る感覚を優先します。教材は全15冊のうち導入に近い章だけを抜粋提示し、「あとでどの冊子に戻るか」が分かるように印を付けます。本科ではこの延長線上に、食卓サービスや書簡などへ枝が分かれます。</p>
<h2>終盤：コース地図と試験までの距離</h2>
<p>実技のあとに、3コースの役割を図で示します。未経験から入る場合の起点はエッセンシャルです。出席率80％以上、修了実技試験、再試験1回無料、協会認定資格といった制度も、この枠で簡潔に触れます。体験当日に試験対策まで踏み込むことはありません。あくまで「どの山に登る計画か」を共有する時間です。</p>
<h2>参加後に自分へ問うチェック</h2>
<p>帰宅後は、指摘された動作を再現できたか、長時間の実技に体力が合うか、土日の通学が生活と両立するかを書き出してください。体験で得た感触と、料金・日程・カリキュラムの書面情報を並べたときに初めて、申込の判断材料が揃います。次の一歩は資料請求でも個別の質問でも構いません。体験の価値は、その後の選択が雑にならなくなることにあります。</p>`,
  },
  {
    slug: 'materials-revision-yearend-202411',
    date: '2024年11月15日',
    category: 'update',
    categoryLabel: '更新情報',
    title: '場面設定から始めるロールプレイング授業の設計',
    metaDescription:
      'ロールプレイングで使う場面カードの作り方、講師が実演中に介入する観点、振り返りの記録方法を公開。1日6時間のコマ運営のなかで、再現性のある接遇判断をどう鍛えるかを解説します。',
    keywords: ['ロールプレイング', '接遇訓練', '場面設定', '実技授業', '講師フィードバック', '執事教育'],
    heroImage: '/images/generated/essential-hero-v2-2026.jpg',
    heroAlt: '設定された接遇場面で受講生が応対の実演を行っているところ',
    figures: [
      {
        src: '/images/generated/essential-writing-2026.jpg',
        alt: '場面の条件をノートに整理しながら振り返りを書いている様子',
        caption: '実演後は条件と修正点を短く書き残し、次の場面へ持ち越す',
      },
    ],
    overview: {
      heading: 'ロールプレイング設計の枠組み',
      rows: [
        { label: '入力', value: '場面カード（場所・相手・制約）' },
        { label: '実施', value: '90分コマ内での短時間ローテーション' },
        { label: '修正観点', value: '間・視線・言葉選び・優先順位' },
        { label: '記録', value: '本人用の一行フィードバック' },
        { label: '到達点', value: '修了実技試験で同型の判断ができること' },
      ],
    },
    faq: [
      {
        question: '脚本を丸暗記する訓練ですか？',
        answer:
          'いいえ。条件が変わっても判断が崩れないかを見る訓練です。台詞の固定より、優先順位の付け方を重視します。',
      },
      {
        question: '内気な受講生でも参加できますか？',
        answer:
          'できます。最初は二人組の短い場面から入り、観察役と実演役を交代します。発表会形式にはしません。',
      },
      {
        question: '試験との関係を教えてください。',
        answer:
          '修了実技試験でも、未知の条件を含む場面が出ます。授業中のロールプレイングは、その形式に慣れるための反復です。',
      },
    ],
    related: [
      { title: 'カリキュラム', desc: '実技と座学の配分', href: '/curriculum' },
      { title: '教材について', desc: '全15冊と場面演習の対応', href: '/materials' },
      { title: '修了・認定について', desc: '修了実技試験の考え方', href: '/certificate' },
      { title: '受講料金', desc: 'コース別の費用', href: '/pricing' },
    ],
    body: `<p>接遇の知識を頭に入れても、現場では想定外の順番で出来事が来ます。当校がロールプレイングを授業の中核に置く理由はそこにあります。以下では、場面の作り方と、講師が実演の最中にどこを直すのかを、運営手順として共有します。</p>
<h2>場面カードに書く三要素</h2>
<p>毎回の演習は、場所・相手・制約の三要素から始めます。例として「夕方の玄関／初めて訪れる来客／主人は電話中」のように、情報を意図的に不足させます。受講生は不足を埋める質問と、今すぐ取る行動を自分で選びます。完成された脚本を配らないことで、判断の筋肉を使う時間を確保します。</p>
<h2>実演中に講師が止めるタイミング</h2>
<p>止めに入るのは、主に四つの信号です。間が空きすぎて相手を不安にさせる、視線が手元だけに落ちる、丁寧語の選択が相手との距離に合わない、複数の依頼が来たときに優先順位を誤る、の四つです。止めた直後に長い講義はしません。正しい一手を短く示し、同じ条件でもう一度やってもらいます。定員6名のクラスだから、再演の順番が回る前に熱が冷めません。</p>
<h2>90分を三分する回し方</h2>
<p>1コマ90分を、条件提示と準備、実演と即時修正、記述振り返りに分けます。振り返りでは「何が起きたか」ではなく「次に変える一つ」だけを書かせます。教科書全15冊のうち、その日の場面に対応する章へ戻る印もここで付けます。1日6時間・4コマある日は、後半コマで制約を増やし、朝より判断負荷を上げます。</p>
<h2>本科全体への位置づけ</h2>
<p>ロールプレイングは単発の遊びではなく、修了実技試験と同型の思考を積み上げる装置です。エッセンシャルで基礎場面を固め、アドバンスとマスターで相手の数や時間圧力を増やします。協会認定資格の先にある実務でも、条件が崩れた瞬間に戻れる型を持っているかどうかが差になります。場面の設計図を公開したのは、入学前の方にも「暗記の学校ではない」と伝わるようにするためです。疑問があれば、授業見学や相談の枠で具体例を追加で示します。</p>`,
  },
  {
    slug: 'master-mansion-ops-cohort-202402',
    date: '2024年2月9日',
    category: 'schedule',
    categoryLabel: '開校情報',
    title: '邸宅運営を統括するマスター、2月クラスの学び方',
    metaDescription:
      '2024年2月開校のマスターコース。3ヶ月16日間96時間で邸宅運営の統括判断を磨く回の日程、土日6時間制の進め方、修了実技試験までの申込手順を具体的にまとめました。定員6名・お台場本校。',
    keywords: ['マスターコース', '邸宅運営', '2024年2月開校', '統括判断', '修了実技試験', 'お台場本校'],
    heroImage: '/images/generated/master-hero-2026.jpg',
    heroAlt: '邸宅の応接空間を想定した実習室で統括役の動線を確認する受講生',
    figures: [
      {
        src: '/images/generated/master-service-2026.jpg',
        alt: '複数の依頼が重なる場面で優先順位をホワイトボードに整理している様子',
        caption: '主人・来客・スタッフの三方向を同時に見積もる訓練を重ねる',
      },
    ],
    overview: {
      heading: '2024年2月マスターコースの枠組み',
      rows: [
        { label: '対象コース', value: 'マスター（3ヶ月・16日間・96時間）' },
        { label: '開校', value: '2024年2月下旬・土日開催' },
        { label: '1日の構成', value: '6時間（90分×4コマ）' },
        { label: '定員', value: '6名（最少催行3名）' },
        { label: '会場', value: 'お台場本校' },
        { label: '締切', value: '開校2週間前・定員次第で早期締切' },
      ],
    },
    faq: [
      {
        question: 'マスターから直接申し込めますか？',
        answer:
          '原則はアドバンス修了相当の実技基盤がある方を対象とします。未経験の方はエッセンシャルからの段階受講をご案内します。',
      },
      {
        question: '邸宅運営とは具体的に何を指しますか？',
        answer:
          '来客動線、食事と行事の段取り、スタッフ配置、緊急時の連絡順など、単発の接遇を超えて一日を設計する領域です。',
      },
      {
        question: '欠席が出た場合の扱いは？',
        answer:
          '出席率80％以上が修了要件です。やむを得ない欠席は補講の可否を個別調整し、修了実技試験は再試験1回無料です。',
      },
    ],
    related: [
      { title: 'コース一覧', desc: 'マスターの位置づけを確認', href: '/courses' },
      { title: 'カリキュラム', desc: '96時間の単元構成', href: '/curriculum' },
      { title: '修了・認定について', desc: '協会認定資格と実技試験', href: '/certificate' },
      { title: '入学案内', desc: '申込手順と必要書類', href: '/admission' },
    ],
    body: `<p>2024年2月、お台場本校でマスターコースの新クラスを開校します。対象は邸宅運営を統括する側の判断力を伸ばしたい方です。接遇の型を一通り身につけたあと、「誰が・いつ・何を優先するか」を一日単位で設計する力へ進む回になります。</p>
<h2>日程と学びの密度</h2>
<p>期間は3ヶ月、登校は16日間、総時間96時間です。各日は土日開催で6時間、90分×4コマに区切ります。前半コマで状況整理と役割分担を机上で組み、後半コマで実室に移して動線を検証します。教科書全15冊のうち統括・行事・緊急対応に関わる章を軸に、現場で崩れた条件をその場で組み直す練習を重ねます。</p>
<h2>この回で扱う統括テーマ</h2>
<p>主人の予定変更、来客の重なり、スタッフの欠員といった同時多発の条件をカード化し、受講生が司令塔役を交代で担います。定員6名のため、観察役・実行役・記録役を細かく回しても一人あたりの実演回数が確保できます。最少催行3名を満たさない場合は開校を延期し、無理な詰め込みは行いません。</p>
<h2>修了までの評価</h2>
<p>出席率80％以上を前提に、最終週で修了実技試験を実施します。未知の制約を含む邸宅一日シナリオが出題され、優先順位の説明と実演の両方を見ます。不合格時は再試験1回無料です。協会認定資格の申請手続きは合格発表後に個別案内します。</p>
<h2>申込の流れと締切</h2>
<p>入学案内のフォームから希望回を選び、経歴と既修内容を記入してください。書類確認のあと、必要に応じて短い実技確認の日程を調整します。受付は開校日の2週間前まで。ただし定員に達した時点で締め切るため、2月クラスを検討中の方は早めに空きを問い合わせてください。料金と分割の詳細は受講料金ページ、通学路はアクセスページで照合できます。</p>`,
  },
  {
    slug: 'essential-summer-start-line-202406',
    date: '2024年6月6日',
    category: 'recruitment',
    categoryLabel: '募集情報',
    title: '夏前に型を固める、エッセンシャル6月生の募集',
    metaDescription:
      '2024年6月開校のエッセンシャルコース募集。1ヶ月8日間48時間で立ち居振る舞いと短い接遇の型を固める入門回。土日6時間制・定員6名・お台場本校。申込締切と見学から書類提出までの流れを記載。',
    keywords: ['エッセンシャルコース', '2024年6月開校', '夏の入門', '未経験向け', '48時間', '執事養成'],
    heroImage: '/images/generated/essential-hero-2026.jpg',
    heroAlt: '夏の光が入る実習室で基本の立ち位置を揃えている受講生',
    figures: [
      {
        src: '/images/generated/training-posture-2026.jpg',
        alt: 'お辞儀の角度を鏡の前で講師と並んで確認している場面',
        caption: '入門回では速度より、再現できる型の定着を優先する',
      },
    ],
    overview: {
      heading: '2024年6月エッセンシャル募集概要',
      rows: [
        { label: '対象コース', value: 'エッセンシャル（1ヶ月・8日間・48時間）' },
        { label: '開校', value: '2024年6月中旬・土日開催' },
        { label: '1日の構成', value: '6時間（90分×4コマ）' },
        { label: '定員', value: '6名（最少催行3名）' },
        { label: '会場', value: 'お台場本校' },
        { label: '締切', value: '6月3日必着・先着で受付終了' },
      ],
    },
    faq: [
      {
        question: '完全未経験でも大丈夫ですか？',
        answer:
          '大丈夫です。エッセンシャルは3コースの起点で、立ち方から短い接遇までを順に積みます。前提資格は不要です。',
      },
      {
        question: '夏の予定と重なった場合は？',
        answer:
          '登校は土日の8日間に集中します。欠席が見込まれる日がある方は、申込前に補講の可否を相談してください。',
      },
      {
        question: '上位コースへはそのまま進めますか？',
        answer:
          '修了実技試験に合格し出席率80％以上を満たせば、アドバンスへの接続を個別に案内します。再試験は1回無料です。',
      },
    ],
    related: [
      { title: 'コース一覧', desc: 'エッセンシャルの範囲', href: '/courses' },
      { title: '受講料金', desc: '入門回の費用', href: '/pricing' },
      { title: '入学案内', desc: '書類と選考の手順', href: '/admission' },
      { title: '資料請求（無料）', desc: '日程表を書面で受け取る', href: '/brochure' },
    ],
    body: `<p>夏の予定が固まる前に、基礎の型だけ先に置いておきたい——その声に応える形で、2024年6月にエッセンシャルコースを開校します。未経験から入る方を主対象にした入門回で、立ち居振る舞いと短い接遇の骨格を1ヶ月で整えます。</p>
<h2>6月生のスケジュール感</h2>
<p>総時間は48時間、登校8日間、期間はおよそ1ヶ月です。各日は土日に6時間、90分×4コマで進めます。午前は姿勢・歩行・お辞儀、午後は玄関から案内までの短い場面、というように身体が覚える順序で並べています。教科書全15冊のうち導入章を中心に使い、自宅では「次の登校日までに再現する一点」だけを宿題にします。</p>
<h2>夏に向けた入門回の狙い</h2>
<p>暑さや外出が増える季節は、生活リズムが乱れやすい反面、人と会う機会も増えます。だからこそ、先に型を持っておくと現場での修正が早くなります。定員6名・最少催行3名の教室なので、講師が一人ずつ角度と間を見られます。華やかな演出より、同じ動作を安定して出せる状態をゴールに置きます。</p>
<h2>申込から初回登校まで</h2>
<p>募集の締切は6月3日必着、定員に達し次第終了です。流れは資料確認→入学案内フォーム送信→受付メール→初回前日の持ち物連絡、の順。実技確認のための来校が必要な場合のみ、別日を調整します。協会認定資格や修了実技試験の話は初回オリエンテーションで図解し、入門段階で過度な試験対策には入りません。</p>
<h2>向いている検討タイミング</h2>
<p>夏休み前に週末の使い方を決めたい方、秋以降のアドバンスを見据えて土台だけ先に置きたい方に向きます。逆に、長期旅行で8日中の欠席が続く見込みがある方は、次の開校回の方が無理がありません。空き状況はお問い合わせ、通学経路はアクセス、費用は受講料金の各ページで突き合わせたうえで判断してください。締切直前は返信が集中するため、質問がある方は余裕を持って連絡をください。</p>`,
  },
];
